import { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';

/**
 * Componente genérico para la barra de acciones inferior de los formularios.
 * Muestra botones de navegación y acciones de forma centrada y responsiva.
 * Genera automáticamente un menú flotante sincronizado cuando el usuario hace scroll hacia arriba.
 *
 * @param {Object} props - Propiedades del componente
 * 
 * --- NAVEGACIÓN ---
 * @param {Function} [props.onPrev=null] - Función a ejecutar al hacer clic en "Anterior". Si es null, no se muestra.
 * @param {Function} [props.onNext=null] - Función a ejecutar al hacer clic en "Siguiente". Si es null, no se muestra.
 * 
 * --- ACCIONES DEL FORMULARIO (Botones por defecto) ---
 * @param {boolean} [props.onSave=false] - Define si se muestra el botón "Guardar" (type="submit").
 * @param {Function} [props.onGenerate=null] - Función para el botón "Formulario" (Imprimir/Generar PDF).
 * @param {Function} [props.onSend=null] - Función para el botón "Enviar".
 * @param {Function} [props.onAttachments=null] - Función para el botón "Anexos" / "Ver Anexos".
 * 
 * --- ESTADOS ---
 * @param {boolean} [props.loading=false] - Estado global de carga (deshabilita botones estándar y muestra spinner).
 * @param {boolean} [props.isReadOnly=false] - Modo solo lectura. Si es true, oculta Guardar/Generar/Enviar.
 * @param {boolean} [props.enableExtras=false] - Habilita los botones extra (Formulario, Enviar, Anexos). Equivalente a (id != null).
 * 
 * --- INYECCIONES PERSONALIZADAS ---
 * @param {Array<Object>} [props.customButtons=[]] - Arreglo de objetos para configurar botones extra (se renderizan estáticos y flotantes).
 * @param {string} props.customButtons[].label - Texto del botón (se usa como tooltip en la vista flotante).
 * @param {string} props.customButtons[].icon - Icono de PrimeIcons (ej. 'pi pi-print').
 * @param {Function} props.customButtons[].onClick - Acción al hacer clic.
 * @param {boolean} [props.customButtons[].loading] - Muestra spinner independiente y deshabilita el botón.
 * @param {boolean} [props.customButtons[].disabled] - Deshabilita el botón manualmente.
 * @param {string} [props.customButtons[].className] - Clases extra (ej. 'p-button-warning'). Responsive ('w-full') se inyecta automático.
 * @param {string} [props.customButtons[].type='button'] - Tipo de botón ('button', 'submit').
 * @param {string} [props.customButtons[].tooltip] - Tooltip personalizado (por defecto usa el `label`).
 * 
 * @param {string} [props.layoutVersion='v1'] - 'v1' para mantener compatibilidad con versiones anteriores. 'v2' para el nuevo orden y paleta de colores.
 *
 * @example
 * // Ejemplo de uso con botones personalizados:
 * <BottomActionBar 
 *    onPrev={handlePrev}
 *    onSave={true}
 *    enableExtras={id != null}
 *    customButtons={[
 *        {
 *            label: 'Licitud Fondo',
 *            icon: 'pi pi-print',
 *            onClick: () => generarReporte('declaracion'),
 *            loading: loadReport,
 *            className: 'p-button-warning'
 *        }
 *    ]}
 * />
 */
export const BottomActionBar = ({ onPrev = null, onNext = null, onSave = false, onGenerate = null,
    onSend = null, onAttachments = null, loading = false, isReadOnly = false, enableExtras = false,
    customButtons = [], formId = null, layoutVersion = 'v1' }) => {
    const bottomRef = useRef(null);
    const [mostrarFlotante, setMostrarFlotante] = useState(false);

    // Observer para detectar cuándo la barra inferior sale de la pantalla
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { setMostrarFlotante(!entry.isIntersecting); },
            { threshold: 0 } // Se dispara en el instante en que un píxel del div deja de verse
        );
        const currentRef = bottomRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, []);

    // Construcción de arreglo de botones
    const btnLeft = onPrev ? {
        id: 'prev',
        label: 'Anterior',
        icon: 'pi pi-chevron-left',
        onClick: onPrev,
        className: 'p-button-outlined p-button-secondary bg-white'
    } : null;

    const btnRight = onNext ? {
        id: 'next',
        label: 'Siguiente',
        icon: 'pi pi-chevron-right',
        iconPos: 'right',
        type: isReadOnly ? 'button' : 'submit',
        form: isReadOnly ? undefined : formId,
        onClick: isReadOnly ? onNext : undefined,
        loading: loading, className: 'p-button-primary px-4',
        tooltip: isReadOnly ? 'Siguiente' : 'Guardar y Siguiente',
    } : null;

    const buildCenterButtons = () => {
        const buttons = [];
        if (layoutVersion === 'v1') {
            // Agregamos los botones personalizados primero
            customButtons.forEach((btn, index) => {
                buttons.push({ ...btn, id: `custom-${index}` });
            });

            // Lógica de botones por defecto
            if (isReadOnly) {
                if (onAttachments) buttons.push({ id: 'att-ro', label: 'Ver Anexos', icon: 'pi pi-copy', onClick: onAttachments, className: 'p-button-warning' });
            } else {
                if (onSave) buttons.push({ id: 'save', label: 'Guardar', type: 'submit', icon: 'pi pi-save', loading, className: 'p-button-warning' });

                if (enableExtras) {
                    if (onGenerate) buttons.push({ id: 'gen', label: 'Formulario', icon: 'pi pi-print', onClick: onGenerate, loading, className: 'p-button-warning' });
                    if (onSend) buttons.push({ id: 'send', label: 'Enviar', icon: 'pi pi-send', onClick: onSend, loading, className: 'p-button-warning' });
                    if (onAttachments) buttons.push({ id: 'att', label: 'Anexos', icon: 'pi pi-copy', onClick: onAttachments, loading, className: 'p-button-warning' });
                }
            }
            return buttons;
        }

        if (layoutVersion === 'v2') {
            if (isReadOnly) {
                if (onAttachments) buttons.push({ id: 'att-ro', label: 'Ver Anexos', icon: 'pi pi-paperclip', onClick: onAttachments, className: 'p-button-info p-button-outlined bg-white' });
            } else {
                if (onSave) buttons.push({ id: 'save', label: 'Guardar', type: 'submit', icon: 'pi pi-save', loading, className: 'p-button-primary' });

                if (enableExtras) {
                    if (onGenerate) buttons.push({ id: 'gen', label: 'Formulario', icon: 'pi pi-print', onClick: onGenerate, loading, className: 'p-button-secondary' });
                }

                customButtons.forEach((btn, index) => {
                    // Si el usuario no mandó clase, le damos un azul "outlined" (bordeado) para que no sature la vista
                    buttons.push({ ...btn, id: `custom-${index}`, className: btn.className || 'p-button-outlined p-button-secondary bg-white' });
                });

                if (enableExtras) {
                    if (onAttachments) buttons.push({ id: 'att', label: 'Anexos', icon: 'pi pi-paperclip', onClick: onAttachments, loading, className: 'p-button-info' });
                    if (onSend) buttons.push({ id: 'send', label: 'Enviar', icon: 'pi pi-send', onClick: onSend, loading, className: 'p-button-success' });
                }
            }
            return buttons;
        }

    };

    const centerButtons = buildCenterButtons();

    // Arreglo con TODOS los botones para renderizar en el menú flotante
    const allButtons = [btnLeft, ...centerButtons, btnRight].filter(Boolean);

    // Renderiza botones para la barra estática, ojo con la propiedad p-fluid
    const renderStaticButton = (btn) => (
        <Button key={btn.id} form={btn.form} label={btn.label} icon={btn.loading ? 'pi pi-spin pi-spinner' : btn.icon} iconPos={btn.iconPos || 'left'} type={btn.type || 'button'}
            onClick={btn.onClick} disabled={btn.disabled || btn.loading} className={`${btn.className || 'p-button-primary'} w-full md:w-auto`} tooltipOptions={btn.tooltipOptions || { position: 'top' }} />
    );

    // Renderiza botones para la barra flotante (iconos redondos)
    const renderFloatingButton = (btn) => (
        <Button key={btn.id} form={btn.form} icon={btn.loading ? 'pi pi-spin pi-spinner' : btn.icon} type={btn.type || 'button'} onClick={btn.onClick} disabled={btn.disabled || btn.loading}
            rounded className={`${btn.className || 'p-button-primary'} shadow-4`} style={{ width: '45px', height: '45px' }} tooltip={btn.tooltip || btn.label} tooltipOptions={{ position: 'left' }} />
    );

    return (
        <>
            <div ref={bottomRef} className="flex flex-column md:flex-row align-items-center mt-4 mb-2 px-2 gap-3 w-full" style={{ minHeight: '50px' }}>
                <div className="w-full md:w-auto flex justify-content-start flex-1">
                    {btnLeft && renderStaticButton(btnLeft)}
                </div>

                <div className="w-full md:w-auto flex flex-wrap justify-content-center gap-2">
                    {centerButtons.map(renderStaticButton)}
                </div>

                <div className="w-full md:w-auto flex justify-content-end flex-1">
                    {btnRight && renderStaticButton(btnRight)}
                </div>
            </div>

            {/* MENÚ FLOTANTE DINÁMICO */}
            {mostrarFlotante && (
                <div className="flex gap-2 fadein scalein" style={{ position: 'fixed', bottom: '20px', right: '30px', zIndex: 999 }}>
                    {allButtons.map(renderFloatingButton)}
                </div>
            )}
        </>
    );
};